// ============================================================================
// ConsultantSnapshotManager.jsx
//
// Provides Save and Retrieve functionality for all consultant tabs.
//
// SAVE: A "Save Advisor Snapshot" button on each generated tab opens a small modal
//       where the user can enter an optional description, then confirms save.
//
// RETRIEVE: A "Advisor Snapshots" button (top-right of dashboard) opens a panel
//           listing all saved snapshots for this model. Each row shows:
//             - tab label, description, date/time
//           Actions: View (loads HTML into HtmlPreviewFrame modal), Delete, Edit description.
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Archive, BookOpen, Check, ChevronDown, Clock, Download, Edit2, Eye, Printer, Save, Trash2, X } from 'lucide-react';
import HtmlPreviewFrame from '@process-advisor/process-miner/consultant/HtmlPreviewFrame';
import {
  deleteSnapshot,
  getSnapshot,
  listSnapshots,
  saveSnapshot,
  updateSnapshotDescription,
} from '@process-advisor/process-miner/consultant/consultantSnapshotService';

// ── Colour map per tab ────────────────────────────────────────────────────────
const TAB_COLORS = {
  processHealth:       '#0052CC',
  trendAnalysis:       '#0C66E4',
  aiIntelligence:      '#1868DB',
  aiExecutionStrategy: '#0065FF',
  automationPacks:     '#0558CB',
  consultingReport:    '#0747A6',
};

const tabColor = (tab) => TAB_COLORS[tab] || '#0052CC';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (ms) => {
  if (!ms) return '';
  const d = new Date(Number(ms));
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const Spinner = () => (
  <span style={{
    display: 'inline-block', width: 14, height: 14,
    border: '2px solid #dfe1e6', borderTopColor: '#0052CC',
    borderRadius: '50%', animation: 'spin 0.7s linear infinite',
  }} />
);

const spinStyle = `@keyframes spin { to { transform: rotate(360deg); } }`;

// ── Save Advisor Snapshot Modal ───────────────────────────────────────────────────────
export const SaveSnapshotModal = ({
  isOpen, onClose, onSave,
  tab, tabLabel, saving,
}) => {
  const [description, setDescription] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDescription('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(description.trim() || null);
  };

  const color = tabColor(tab);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 3000,
      backgroundColor: 'rgba(9,30,66,0.54)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <style>{spinStyle}</style>
      <div style={{
        background: '#fff', borderRadius: 10, width: 420,
        boxShadow: '0 8px 32px rgba(9,30,66,0.25)',
        overflow: 'hidden',
      }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid #EBECF0',
          background: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 6, background: color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Save size={14} color="white" />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#172B4D' }}>Save Advisor Snapshot</div>
              <div style={{ fontSize: 11, color: '#6B778C' }}>{tabLabel}</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#6B778C', padding: 4, borderRadius: 4,
          }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 18px 20px' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#172B4D', display: 'block', marginBottom: 6 }}>
            Description <span style={{ color: '#6B778C', fontWeight: 400 }}>(optional)</span>
          </label>
          <input
            ref={inputRef}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            placeholder={`e.g. "Initial assessment — pre-sprint 14"`}
            maxLength={500}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 10px', border: '2px solid #dfe1e6', borderRadius: 4,
              fontSize: 13, color: '#172B4D', outline: 'none',
              fontFamily: 'inherit',
            }}
            onFocus={(e) => e.target.style.borderColor = color}
            onBlur={(e) => e.target.style.borderColor = '#dfe1e6'}
          />
          <div style={{ fontSize: 11, color: '#6B778C', marginTop: 6 }}>
            Saved as: <strong>{tabLabel}</strong> · {fmtDate(Date.now())}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '10px 18px', borderTop: '1px solid #EBECF0', background: '#FAFBFC',
        }}>
          <button onClick={onClose} style={{
            background: '#fff', color: '#42526E', border: '1px solid #DFE1E6',
            padding: '6px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: saving ? '#dfe1e6' : color,
              color: saving ? '#97a0af' : '#fff',
              border: 'none', padding: '6px 16px', borderRadius: 4,
              fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {saving ? <><Spinner /> Saving…</> : <><Check size={13} /> Save Advisor Snapshot</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Snapshot List Panel ───────────────────────────────────────────────────────
export const SnapshotListPanel = ({
  isOpen, onClose,
  modelId, AppVersion,
}) => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [viewing, setViewing] = useState(null);   // { html, tabLabel, description, created_at }
  const [editing, setEditing] = useState(null);   // snapshot id
  const [editVal, setEditVal] = useState('');
  const [filterTab, setFilterTab] = useState('all');

  const load = useCallback(async () => {
    if (!modelId || !isOpen) return;
    setLoading(true);
    try {
      const res = await listSnapshots({ modelId, AppVersion });
      setSnapshots(res.snapshots || []);
    } catch (err) {
      console.error('SnapshotListPanel load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [modelId, AppVersion, isOpen]);

  useEffect(() => { load(); }, [load]);

  // Reset transient state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setViewing(null);
      setEditing(null);
      setFilterTab('all');
    }
  }, [isOpen]);

  const handleView = async (snap) => {
    try {
      const res = await getSnapshot({ id: snap.id, modelId, AppVersion });
      if (res.snapshot?.html) {
        setViewing(res.snapshot);
      }
    } catch (err) {
      console.error('handleView failed:', err);
    }
  };

  const handleDelete = async (snap) => {
    if (!window.confirm(`Delete snapshot "${snap.description || snap.tab_label}"?`)) return;
    setDeleting(snap.id);
    try {
      const result = await deleteSnapshot({ id: snap.id, modelId, AppVersion });
      if (!result?.success) throw new Error('Delete failed');
      setSnapshots((prev) => prev.filter((s) => s.id !== snap.id));
    } finally {
      setDeleting(null);
    }
  };

  const handleDownload = async (snap) => {
    try {
      const res = await getSnapshot({ id: snap.id, modelId, AppVersion });
      if (!res.snapshot?.html) return;
      const blob = new Blob([res.snapshot.html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const label = (snap.description || snap.tab_label)
        .replace(/[^a-z0-9]/gi, '-').toLowerCase().slice(0, 50);
      const date = new Date(Number(snap.created_at))
        .toISOString().slice(0, 10);
      a.download = `snapshot-${label}-${date}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('handleDownload failed:', err);
    }
  };

  const handlePdf = async (snap) => {
    try {
      const res = await getSnapshot({ id: snap.id, modelId, AppVersion });
      if (!res.snapshot?.html) return;
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;width:0;height:0;border:none;left:-9999px;top:-9999px;';
      document.body.appendChild(iframe);
      iframe.contentDocument.open();
      iframe.contentDocument.write(res.snapshot.html);
      iframe.contentDocument.close();
      iframe.contentWindow.focus();
      setTimeout(() => {
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 400);
    } catch (err) {
      console.error('handlePdf failed:', err);
    }
  };

  const handleEditSave = async (snap) => {
    try {
      const result = await updateSnapshotDescription({ id: snap.id, modelId, description: editVal, AppVersion });
      if (!result?.success) throw new Error('Update failed');
      setSnapshots((prev) =>
        prev.map((s) => s.id === snap.id ? { ...s, description: editVal } : s)
      );
    } finally {
      setEditing(null);
    }
  };

  // Unique tabs for filter
  const tabs = ['all', ...Array.from(new Set(snapshots.map((s) => s.tab)))];
  const filtered = filterTab === 'all' ? snapshots : snapshots.filter((s) => s.tab === filterTab);

  return (
    <>
      <style>{spinStyle}</style>
      {/* Backdrop — always in DOM, shown/hidden via display */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        backgroundColor: 'rgba(9,30,66,0.40)',
        display: isOpen ? 'block' : 'none',
        pointerEvents: isOpen ? 'auto' : 'none',
      }} onClick={onClose} />

      {/* Panel — always in DOM, slid in/out via transform */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 2001,
        width: 560, background: '#fff',
        boxShadow: '-4px 0 24px rgba(9,30,66,0.18)',
        display: 'flex', flexDirection: 'column',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease',
        pointerEvents: isOpen ? 'auto' : 'none',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid #EBECF0',
          background: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'linear-gradient(135deg, #0052CC 0%, #0C66E4 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Archive size={16} color="white" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#172B4D' }}>Advisor Snapshots</div>
              <div style={{ fontSize: 11, color: '#6B778C' }}>
                {snapshots.length} saved · click a row to view
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#6B778C', padding: 4,
          }}>
            <X size={18} />
          </button>
        </div>

        {/* Tab filter */}
        {tabs.length > 2 && (
          <div style={{
            display: 'flex', gap: 6, padding: '10px 18px', flexWrap: 'wrap',
            borderBottom: '1px solid #EBECF0', flexShrink: 0,
          }}>
            {tabs.map((t) => (
              <button key={t} onClick={() => setFilterTab(t)} style={{
                padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                cursor: 'pointer', border: 'none',
                background: filterTab === t ? '#0052CC' : '#EBECF0',
                color: filterTab === t ? '#fff' : '#42526E',
              }}>
                {t === 'all' ? 'All' : snapshots.find((s) => s.tab === t)?.tab_label || t}
              </button>
            ))}
          </div>
        )}

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <Spinner />
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '48px 24px', color: '#6B778C', textAlign: 'center',
            }}>
              <BookOpen size={36} strokeWidth={1.2} style={{ marginBottom: 12, opacity: 0.4 }} />
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No snapshots yet</div>
              <div style={{ fontSize: 12 }}>
                Generate content in any tab, then click <strong>Save Advisor Snapshot</strong> to archive it here.
              </div>
            </div>
          )}
          {!loading && filtered.map((snap) => {
            const color = tabColor(snap.tab);
            const isEdit = editing === snap.id;
            return (
              <div key={snap.id} style={{
                borderBottom: '1px solid #F4F5F7',
                padding: '12px 18px',
                transition: 'background 0.1s',
              }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F8F9FA'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  {/* Colour dot */}
                  <div style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: color, flexShrink: 0, marginTop: 6,
                  }} />
                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Tab label + time */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '1px 7px',
                        borderRadius: 999, background: `${color}18`, color,
                        border: `1px solid ${color}33`,
                      }}>
                        {snap.tab_label}
                      </span>
                      <span style={{ fontSize: 11, color: '#97A0AF', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Clock size={10} />
                        {fmtDate(snap.created_at)}
                      </span>
                    </div>

                    {/* Description / edit */}
                    {isEdit ? (
                      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                        <input
                          value={editVal}
                          onChange={(e) => setEditVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleEditSave(snap);
                            if (e.key === 'Escape') setEditing(null);
                          }}
                          autoFocus
                          maxLength={500}
                          style={{
                            flex: 1, padding: '4px 8px', border: `2px solid ${color}`,
                            borderRadius: 4, fontSize: 12, fontFamily: 'inherit', outline: 'none',
                          }}
                        />
                        <button onClick={() => handleEditSave(snap)} style={{
                          background: color, color: '#fff', border: 'none',
                          padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                        }}>Save</button>
                        <button onClick={() => setEditing(null)} style={{
                          background: '#EBECF0', color: '#42526E', border: 'none',
                          padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
                        }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: snap.description ? '#172B4D' : '#97A0AF', fontStyle: snap.description ? 'normal' : 'italic' }}>
                        {snap.description || 'No description'}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {!isEdit && (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button
                        onClick={() => handleView(snap)}
                        title="View snapshot"
                        style={{
                          background: '#E9F2FF', border: 'none', padding: '5px 8px',
                          borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center',
                          color: '#0052CC',
                        }}>
                        <Eye size={13} />
                      </button>
                      <button
                        onClick={() => handleDownload(snap)}
                        title="Download as HTML"
                        style={{
                          background: '#E3FCEF', border: 'none', padding: '5px 8px',
                          borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center',
                          color: '#006644',
                        }}>
                        <Download size={13} />
                      </button>
                      <button
                        onClick={() => handlePdf(snap)}
                        title="Print / Save as PDF"
                        style={{
                          background: '#EAE6FF', border: 'none', padding: '5px 8px',
                          borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center',
                          color: '#5243AA',
                        }}>
                        <Printer size={13} />
                      </button>
                      <button
                        onClick={() => { setEditing(snap.id); setEditVal(snap.description || ''); }}
                        title="Edit description"
                        style={{
                          background: '#F4F5F7', border: 'none', padding: '5px 8px',
                          borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center',
                          color: '#42526E',
                        }}>
                        <Edit2 size={13} />
                      </button>
                      <button
                        onClick={() => handleDelete(snap)}
                        disabled={deleting === snap.id}
                        title="Delete snapshot"
                        style={{
                          background: '#FFEBE6', border: 'none', padding: '5px 8px',
                          borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center',
                          color: '#BF2600',
                        }}>
                        {deleting === snap.id ? <Spinner /> : <Trash2 size={13} />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '10px 18px', borderTop: '1px solid #EBECF0',
          background: '#FAFBFC', fontSize: 11, color: '#97A0AF', flexShrink: 0,
        }}>
          Snapshots are stored per process model and persist across sessions.
        </div>
      </div>

      {/* View modal */}
      {viewing && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 3000,
          backgroundColor: 'rgba(9,30,66,0.7)',
          display: 'flex', flexDirection: 'column',
        }} onClick={() => setViewing(null)}>
          <div style={{
            background: '#fff', borderRadius: 10, margin: '20px auto',
            width: '90%', maxWidth: 1000, maxHeight: 'calc(100vh - 40px)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(9,30,66,0.3)',
          }} onClick={(e) => e.stopPropagation()}>
            {/* View header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 18px', borderBottom: '1px solid #EBECF0',
              background: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)',
              flexShrink: 0,
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#172B4D' }}>
                  {viewing.tab_label}
                </div>
                <div style={{ fontSize: 11, color: '#6B778C' }}>
                  {viewing.description || 'No description'} · {fmtDate(viewing.created_at)}
                </div>
              </div>
              <button onClick={() => setViewing(null)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#6B778C',
              }}>
                <X size={18} />
              </button>
            </div>
            {/* View content */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <HtmlPreviewFrame html={viewing.html} title={viewing.tab_label} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Advisor Snapshots button for dashboard header ───────────────────────────────
export const SnapshotHeaderButton = ({ onClick, count }) => (
  <button
    onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 12px', borderRadius: 4,
      background: '#fff', border: '1px solid #DFE1E6',
      fontSize: 12, fontWeight: 600, color: '#172B4D',
      cursor: 'pointer', transition: 'all 0.15s',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f4ff'; e.currentTarget.style.borderColor = '#0052CC'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#DFE1E6'; }}
    title="View saved snapshots"
  >
    <Archive size={14} />
    Advisor Snapshots
    {count > 0 && (
      <span style={{
        background: '#0052CC', color: '#fff',
        borderRadius: 999, fontSize: 10, fontWeight: 800,
        padding: '1px 6px', minWidth: 16, textAlign: 'center',
      }}>
        {count}
      </span>
    )}
  </button>
);

// ── Save Advisor Snapshot button for individual tabs ──────────────────────────────────
export const SaveSnapshotButton = ({ onClick, color = '#0052CC' }) => (
  <button
    onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
      backgroundColor: '#fff', color,
      border: `1px solid ${color}`, padding: '6px 12px', borderRadius: 4,
      minHeight: 30,
      fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    }}
    title="Save a snapshot of this content"
  >
    <Save size={12} />
    Save Advisor Snapshot
  </button>
);

export default SnapshotListPanel;
